import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea,
} from 'recharts';
import { ArrowLeft, Download, Copy, X } from 'lucide-react';
import { getTrackingPeriodsByMatch, comparePeriods } from '../../api';
import { useUrlParam } from '../../hooks/useUrlParam';
import { downloadBlob } from '../../pdfExport';

// Period Comparison (2026-09-28) — "Advanced comparison" from the Team Comparison page. Heracles
// vs the opponent per block of 5-minute KNVB tracking periods, at team, line or player level.
// Every number, diff, leader and note comes from POST /api/tracking/periods/compare
// (services/trackingPeriods.js); this page only builds the blocks and renders.
//
// State lives in the URL (session_id, basis shared with Team Comparison; preset, blocks, blabels,
// level, metric, unit) so a view can be shared and reopened. Structure follows In-Match Analysis
// (recharts, URL-synced controls); visuals follow the Team Comparison card (design 2a tokens).

// Design tokens — same as TeamComparison.js (design 2a).
const INK = '#1B2A41';
const PAGE_BG = '#EEF2F7', CARD_BG = '#FFF', TRACK_BG = '#F2F5F9', HAIRLINE = '#EEF2F7';
const HERACLES = '#C81E3C', OPPONENT = '#35608F', TIE_COLOR = '#4C607A';
const MUTED_LIGHT = '#5B6E86', SECONDARY_LIGHT = '#566A82', MUTED_DARK = '#AEBED2';
const FONT_DISPLAY = "'Barlow Condensed', sans-serif";
const FONT_BODY = "'Barlow', Helvetica, sans-serif";
const FONT_MONO = "'IBM Plex Mono', monospace";
const SIDE_COLOR = { heracles: HERACLES, opponent: OPPONENT };
// The card's leader outline ('3px solid INK', offset 2) drawn as a box-shadow instead: identical on
// screen, and html2canvas renders box-shadow but not outline, so exported PNGs keep it.
const LEADER_RING = `0 0 0 2px #FFF, 0 0 0 5px ${INK}`;

// Block identity colours — deliberately neither Heracles red nor opponent blue.
const BLOCK_COLORS = ['#7C5CBF', '#0E9F8E', '#D98E04', '#64748B', '#C2477F', '#6A9A1F', '#0891B2', '#A16207'];
const blockColor = i => BLOCK_COLORS[i % BLOCK_COLORS.length];
const MAX_CUSTOM_BLOCKS = 8;

const PRESETS = [
  { id: 'halves', label: 'Halves' },
  { id: '15', label: '15 min' },
  { id: '5', label: 'Per 5 min' },
  { id: 'full', label: 'Whole match' },
  { id: 'custom', label: 'Custom' },
];
// Preset -> file-name tag for "Export page", e.g. period-comparison-2026-09-26-vit-15min-team.png
const PRESET_FILE_TAG = { halves: 'halves', 15: '15min', 5: '5min', full: 'full-match', custom: 'custom' };
const LEVELS = [{ id: 'team', label: 'Team' }, { id: 'line', label: 'Lines' }, { id: 'player', label: 'Players' }];
const METRIC_OPTIONS = [
  { id: 'td', label: 'Total Distance' },
  { id: 'd20', label: 'Distance >20 km/h' },
  { id: 's20', label: '# Sprints >20 km/h' },
  { id: 'd25', label: 'Distance >25 km/h' },
  { id: 's25', label: '# Sprints >25 km/h' },
];
const UNIT_OPTIONS = [
  { id: 'per_player_5min', label: 'per player per 5 min' },
  { id: 'per_player_min', label: 'per player per min' },
  { id: 'block_total', label: 'block total' },
];
const LINE_OPTIONS = [{ id: 'defence', label: 'Defence' }, { id: 'midfield', label: 'Midfield' }, { id: 'attack', label: 'Attack' }];
const FACTOR_SOURCE_LABEL = { match: 'this match', pooled: 'pooled', seed: 'seed', none: 'none' };

const ABBREV_OVERRIDES = { 'Heracles Almelo': 'HER' };
function teamAbbrev(name) {
  if (!name) return '???';
  if (ABBREV_OVERRIDES[name]) return ABBREV_OVERRIDES[name];
  const stripped = name.replace(/\b(FC|SC|Jong|De)\b/gi, ' ').replace(/\s+/g, ' ').trim();
  return (stripped || name).slice(0, 3).toUpperCase();
}

const fmtVal = (v, dec) => (v == null || Number.isNaN(v) ? '–' : v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec }));
const fmtMin = m => `${Math.round(m).toLocaleString('en-US')}′`;

// '45+0-45+5' -> { base: 45, stoppage: 0 }; '45-50' -> { base: 45, stoppage: null }.
function parsePeriodLabel(label) {
  const start = String(label).split('-')[0];
  const [base, stoppage] = start.split('+');
  return { base: Number(base), stoppage: stoppage == null ? null : Number(stoppage) };
}
// '0-5' .. '10-15' -> '0-15'. Same derivation as the server's autoBlockLabel.
function autoLabel(labels) {
  if (!labels.length) return '';
  return `${labels[0].split('-')[0]}-${labels[labels.length - 1].split('-')[1]}`;
}

function presetBlocks(preset, periods) {
  if (!periods.length) return [];
  if (preset === 'halves') {
    return [1, 2]
      .map(h => ({ id: `h${h}`, label: h === 1 ? '1st half' : '2nd half', periodIndexes: periods.filter(p => p.half === h).map(p => p.index) }))
      .filter(b => b.periodIndexes.length);
  }
  if (preset === '5') return periods.map(p => ({ id: `p${p.index}`, label: p.label, periodIndexes: [p.index] }));
  if (preset === 'full') return [{ id: 'all', label: 'Whole match', periodIndexes: periods.map(p => p.index) }];
  if (preset === '15') {
    // Three regular periods per block; stoppage periods join the last block of their half.
    const groups = [];
    let current = null;
    for (const p of periods) {
      const { base, stoppage } = parsePeriodLabel(p.label);
      const key = stoppage === null ? `${p.half}-${Math.floor(base / 15)}` : null;
      if (key !== null && (!current || current.key !== key)) {
        current = { key, half: p.half, periods: [] };
        groups.push(current);
      }
      if (key === null && (!current || current.half !== p.half)) {
        current = { key: `${p.half}-stoppage`, half: p.half, periods: [] };
        groups.push(current);
      }
      current.periods.push(p);
    }
    return groups.map((g, i) => ({ id: `q${i}`, label: autoLabel(g.periods.map(p => p.label)), periodIndexes: g.periods.map(p => p.index) }));
  }
  return [];
}

// Custom blocks in the URL: blocks=0.1.2_3.4.5 (period indexes per block), blabels=A|B.
function parseCustomBlocks(raw, rawLabels, periods) {
  if (!raw) return [];
  const valid = new Set(periods.map(p => p.index));
  const labelOf = new Map(periods.map(p => [p.index, p.label]));
  const labels = rawLabels ? rawLabels.split('|') : [];
  const taken = new Set();
  return raw.split('_').map((s, i) => {
    const idx = s.split('.').map(Number).filter(n => Number.isInteger(n) && valid.has(n) && !taken.has(n));
    idx.forEach(n => taken.add(n));
    idx.sort((a, b) => a - b);
    return { id: `c${i}`, label: labels[i] || autoLabel(idx.map(n => labelOf.get(n))), periodIndexes: idx, customLabel: !!labels[i] };
  }).filter(b => b.periodIndexes.length);
}
function serializeCustomBlocks(blocks) {
  return {
    blocks: blocks.map(b => b.periodIndexes.join('.')).join('_'),
    labels: blocks.map(b => (b.customLabel ? b.label.replace(/\|/g, '') : '')).join('|'),
  };
}

// Contiguous runs of period indexes, for shading a (possibly non-contiguous) block on the chart.
function runs(indexes) {
  const out = [];
  for (const i of [...indexes].sort((a, b) => a - b)) {
    const last = out[out.length - 1];
    if (last && i === last[1] + 1) last[1] = i; else out.push([i, i]);
  }
  return out;
}

// ── Small UI pieces ─────────────────────────────────────────────────────────
const ctlBtn = active => ({
  padding: '7px 12px', borderRadius: 8, border: `1px solid ${active ? '#3b82f6' : 'var(--border-strong)'}`,
  background: active ? '#3b82f6' : 'transparent', color: active ? '#fff' : 'var(--text-muted)',
  cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap',
});
const selStyle = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', cursor: 'pointer', fontFamily: 'inherit' };
const cardStyle = { maxWidth: 1100, width: '100%', background: CARD_BG, borderRadius: 12, boxShadow: '0 1px 2px rgba(27,42,65,.08)', boxSizing: 'border-box' };
const sectionTitle = { fontFamily: FONT_DISPLAY, fontSize: 19, fontWeight: 600, color: INK };
const monoNote = { fontFamily: FONT_MONO, fontSize: 10, color: MUTED_LIGHT };

function Segmented({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map(o => <button key={o.id} style={ctlBtn(value === o.id)} onClick={() => onChange(o.id)}>{o.label}</button>)}
    </div>
  );
}

function Note({ children, tone = 'info' }) {
  const colors = tone === 'warn'
    ? { bg: '#FFF7E6', border: '#F5D08A', fg: '#7A4B00' }
    : { bg: '#EEF4FB', border: '#C7D7EA', fg: '#284766' };
  return (
    <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8, padding: '8px 12px', fontFamily: FONT_BODY, fontSize: 13, color: colors.fg }}>
      {children}
    </div>
  );
}

function LeaderPill({ cmp, names }) {
  if (!cmp || cmp.leader == null) return <div style={{ height: 22 }} />;
  const color = cmp.leader === 'tie' ? TIE_COLOR : SIDE_COLOR[cmp.leader];
  const text = cmp.leader === 'tie' ? 'TIE' : names[cmp.leader];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', height: 22 }}>
      <span style={{ fontFamily: FONT_DISPLAY, fontSize: 12, fontWeight: 700, letterSpacing: '.08em', color: '#FFF', background: color, padding: '1px 6px', borderRadius: 3 }}>{text}</span>
      <span style={{ fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 700, color }}>{cmp.deltaLabel}</span>
    </div>
  );
}

// One Heracles/opponent bar pair on a shared scale. Leader bar gets the card's outline.
function BarPair({ heracles, opponent, leader, max, dec, height = 170 }) {
  const bar = (side, v) => {
    const h = v == null || !(max > 0) ? 0 : Math.max((v / max) * height, 2);
    return (
      <div style={{ width: 34, height, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: 13, fontWeight: 700, color: SIDE_COLOR[side], marginBottom: 3, whiteSpace: 'nowrap' }}>{fmtVal(v, dec)}</div>
        <div style={{
          width: '100%', height: h, background: v == null ? 'transparent' : SIDE_COLOR[side], borderRadius: '3px 3px 0 0',
          boxShadow: leader === side ? LEADER_RING : 'none',
        }} />
      </div>
    );
  };
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', justifyContent: 'center', borderBottom: `2px solid ${INK}` }}>
      {bar('heracles', heracles)}
      {bar('opponent', opponent)}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function PeriodComparison({ onNavigate }) {
  const [sessionIdRaw] = useUrlParam('session_id', '');
  const sessionId = sessionIdRaw === '' ? null : Number(sessionIdRaw);
  const [basis, setBasis] = useUrlParam('basis', 'heralabx');
  const [preset, setPreset] = useUrlParam('preset', 'halves');
  const [customRaw, setCustomRaw] = useUrlParam('blocks', '');
  const [customLabelsRaw, setCustomLabelsRaw] = useUrlParam('blabels', '');
  const [level, setLevel] = useUrlParam('level', 'team');
  const [metric, setMetric] = useUrlParam('metric', 'td');
  const [unit, setUnit] = useUrlParam('unit', 'per_player_5min');

  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestSeq = useRef(0);

  // Custom-mode selection being built (not yet a block).
  const [pending, setPending] = useState([]);
  const lastClicked = useRef(null);
  const drag = useRef(null); // { add: boolean } while the mouse is down on the strip

  const [sort, setSort] = useState({ key: 'b0', dir: 'desc' });
  const [teamFilter, setTeamFilter] = useState('all');
  const [lineFilter, setLineFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [copied, setCopied] = useState(null); // which export was just copied ('chart' | 'cards')
  const exportRef = useRef(null); // the block-comparison card (its own PNG / Copy buttons)
  const pageRef = useRef(null);   // the whole analysis ("Export page")
  const cardsRef = useRef(null);  // "All metrics per block" (its own PNG / Copy buttons)

  useEffect(() => {
    if (!sessionId) return;
    setMeta(null);
    setMetaError('');
    getTrackingPeriodsByMatch(sessionId)
      .then(m => setMeta(m))
      .catch(() => setMetaError('No KNVB tracking file linked to this match.'));
  }, [sessionId]);

  const periods = useMemo(() => meta?.periods || [], [meta]);

  const customBlocks = useMemo(
    () => parseCustomBlocks(customRaw, customLabelsRaw, periods),
    [customRaw, customLabelsRaw, periods],
  );
  const blocks = useMemo(
    () => (preset === 'custom' ? customBlocks : presetBlocks(preset, periods)),
    [preset, customBlocks, periods],
  );
  const setCustomBlocks = useCallback(next => {
    const s = serializeCustomBlocks(next);
    setCustomRaw(s.blocks);
    setCustomLabelsRaw(s.labels.replace(/\|+$/, ''));
  }, [setCustomRaw, setCustomLabelsRaw]);

  const ownerOf = useMemo(() => {
    const m = new Map();
    blocks.forEach((b, i) => b.periodIndexes.forEach(pi => m.set(pi, i)));
    return m;
  }, [blocks]);

  // ── Fetch the comparison ─────────────────────────────────────────────────
  const requestKey = JSON.stringify({ id: meta?.trackingMatchId, blocks: blocks.map(b => [b.id, b.label, b.periodIndexes]), level, metric, unit, basis });
  useEffect(() => {
    if (!meta?.trackingMatchId || !meta.hasPeriods || !blocks.length) { setData(null); return; }
    const seq = ++requestSeq.current;
    setLoading(true);
    setError('');
    comparePeriods({
      trackingMatchId: meta.trackingMatchId,
      blocks: blocks.map(b => ({ id: b.id, label: b.label, periodIndexes: b.periodIndexes })),
      level, metric, unit, basis,
    })
      .then(res => { if (seq === requestSeq.current) setData(res); })
      .catch(err => { if (seq === requestSeq.current) { setData(null); setError(err.message); } })
      .finally(() => { if (seq === requestSeq.current) setLoading(false); });
    // requestKey captures every input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);

  // Reset table sort when the block set changes shape.
  useEffect(() => { setSort({ key: 'b0', dir: 'desc' }); }, [blocks.length]);

  // ── Period strip interaction ─────────────────────────────────────────────
  const enterCustom = useCallback(() => {
    if (preset === 'custom') return customBlocks;
    const seeded = blocks.map((b, i) => ({ id: `c${i}`, label: b.label, periodIndexes: b.periodIndexes, customLabel: true }));
    setCustomBlocks(seeded);
    setPreset('custom');
    return seeded;
  }, [preset, customBlocks, blocks, setCustomBlocks, setPreset]);

  const togglePending = (pi, add) => {
    setPending(prev => {
      const has = prev.includes(pi);
      if (add && !has) return [...prev, pi];
      if (!add && has) return prev.filter(x => x !== pi);
      return prev;
    });
  };

  const onChipMouseDown = (p, e) => {
    const current = enterCustom();
    const owner = current.findIndex(b => b.periodIndexes.includes(p.index));
    if (owner >= 0) {
      // Take the period out of its block; an emptied block disappears.
      const next = current.map((b, i) => (i === owner ? { ...b, periodIndexes: b.periodIndexes.filter(x => x !== p.index), customLabel: b.customLabel } : b))
        .filter(b => b.periodIndexes.length)
        .map(b => (b.customLabel ? b : { ...b, label: autoLabel(b.periodIndexes.map(pi => periods.find(x => x.index === pi).label)) }));
      setCustomBlocks(next);
      return;
    }
    if (e.shiftKey && lastClicked.current !== null) {
      const [a, b] = [lastClicked.current, p.index].sort((x, y) => x - y);
      const assigned = new Set(current.flatMap(bl => bl.periodIndexes));
      setPending(prev => [...new Set([...prev, ...periods.filter(x => x.index >= a && x.index <= b && !assigned.has(x.index)).map(x => x.index)])]);
    } else {
      const add = !pending.includes(p.index);
      togglePending(p.index, add);
      drag.current = { add };
    }
    lastClicked.current = p.index;
  };
  const onChipMouseEnter = p => {
    if (!drag.current || ownerOf.has(p.index)) return;
    togglePending(p.index, drag.current.add);
  };
  useEffect(() => {
    const up = () => { drag.current = null; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);
  // Leaving custom mode drops a half-built selection.
  useEffect(() => { if (preset !== 'custom') setPending([]); }, [preset]);

  const addBlock = () => {
    if (!pending.length || customBlocks.length >= MAX_CUSTOM_BLOCKS) return;
    const idx = [...pending].sort((a, b) => a - b);
    const next = [...customBlocks, { id: `c${customBlocks.length}`, label: autoLabel(idx.map(pi => periods.find(x => x.index === pi).label)), periodIndexes: idx, customLabel: false }];
    setCustomBlocks(next);
    setPending([]);
  };
  const renameBlock = (i, label) => {
    setCustomBlocks(customBlocks.map((b, j) => (j === i ? { ...b, label: label || autoLabel(b.periodIndexes.map(pi => periods.find(x => x.index === pi).label)), customLabel: !!label } : b)));
  };
  const removeBlock = i => setCustomBlocks(customBlocks.filter((_, j) => j !== i));

  // ── Export ───────────────────────────────────────────────────────────────
  // Three exports, one capture path. Anything marked data-html2canvas-ignore (controls, the
  // export buttons themselves) is left out. `pin` fixes the cloned element to the desktop layout
  // width, so a multi-card image looks the same whatever the window size.
  const EXPORTS = {
    // The block-comparison card on its own.
    chart: { ref: exportRef, backgroundColor: CARD_BG, suffix: () => `-${PRESET_FILE_TAG[preset] || 'blocks'}-${level}-${metric}` },
    // "All metrics per block": every per-block card as one image.
    cards: {
      ref: cardsRef, backgroundColor: PAGE_BG, suffix: () => `-${PRESET_FILE_TAG[preset] || 'blocks'}-all-metrics`,
      pin: { selector: '[data-export-cards]', style: { width: '1100px', maxWidth: 'none', padding: '16px' } },
    },
    // The whole analysis: header, match shape, block comparison (or player table), cards, notes.
    page: {
      ref: pageRef, backgroundColor: PAGE_BG, suffix: () => `-${PRESET_FILE_TAG[preset] || 'blocks'}-${level}`,
      pin: { selector: '[data-export-root]', style: { width: '1148px', maxWidth: 'none', padding: '24px' } },
    },
  };
  const renderPng = async ({ ref, backgroundColor, pin }) => {
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(ref.current, {
      scale: 2, useCORS: true, backgroundColor, logging: false,
      ...(pin ? {
        windowWidth: 1280,
        onclone: doc => {
          const el = doc.querySelector(pin.selector);
          if (el) Object.assign(el.style, pin.style);
        },
      } : {}),
    });
    return new Promise(res => canvas.toBlob(res, 'image/png'));
  };
  const exportName = suffix => `period-comparison-${String(meta.date).slice(0, 10)}-${teamAbbrev(meta.opponentTeam).toLowerCase()}${suffix}.png`;
  const downloadPng = async which => {
    const target = EXPORTS[which];
    if (!target.ref.current) return;
    setExporting(true);
    try {
      const blob = await renderPng(target);
      if (blob) downloadBlob(blob, exportName(target.suffix()));
    } finally { setExporting(false); }
  };
  const canCopy = typeof window !== 'undefined' && !!window.ClipboardItem && !!navigator.clipboard?.write;
  const copyPng = async which => {
    const target = EXPORTS[which];
    if (!target.ref.current || !canCopy) return;
    setExporting(true);
    try {
      const blob = await renderPng(target);
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      setCopied(which);
      setTimeout(() => setCopied(null), 1800);
    } finally { setExporting(false); }
  };
  const exportButtons = which => (
    <div style={{ display: 'flex', gap: 6 }} data-html2canvas-ignore="true">
      <button style={ctlBtn(false)} onClick={() => downloadPng(which)} disabled={exporting} title="Download as PNG"><span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}><Download size={13} /> PNG</span></button>
      {canCopy && <button style={ctlBtn(false)} onClick={() => copyPng(which)} disabled={exporting} title="Copy to clipboard"><span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}><Copy size={13} /> {copied === which ? 'Copied' : 'Copy'}</span></button>}
    </div>
  );

  // ── Early states ─────────────────────────────────────────────────────────
  const shell = (children, actions = null) => (
    <div style={{ background: PAGE_BG, margin: '-24px', padding: 24, minHeight: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
      <div style={{ maxWidth: 1100, width: '100%', display: 'flex', alignItems: 'center', gap: 8 }}>
        <button style={ctlBtn(false)} onClick={() => onNavigate && onNavigate('gps', 'team-comparison')}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><ArrowLeft size={14} /> Team comparison</span>
        </button>
        {actions && <div style={{ marginLeft: 'auto' }}>{actions}</div>}
      </div>
      {children}
    </div>
  );
  const message = (title, sub) => shell(
    <div style={{ ...cardStyle, padding: 32, textAlign: 'center', fontFamily: FONT_BODY }}>
      <div style={{ fontSize: 15, color: INK, fontWeight: 600 }}>{title}</div>
      {sub && <div style={{ fontSize: 13, color: MUTED_LIGHT, marginTop: 6 }}>{sub}</div>}
    </div>,
  );
  if (!sessionId) return message('No match selected', 'Open this page from "Advanced comparison" on the Team Comparison page.');
  if (metaError) return message(metaError, 'Upload the KNVB fysiek file in the Upload Zone.');
  if (!meta) return shell(<div style={{ color: 'var(--text-dimmed)', fontSize: 14, padding: 40 }}>Loading…</div>);
  if (!meta.hasPeriods) {
    return message('No 5-minute periods for this match', 'This file was imported before periods were stored. Re-upload the KNVB fysiek file (overwrite) in the Upload Zone.');
  }

  const names = { heracles: 'HERACLES', opponent: meta.opponentTeam.toUpperCase() };
  const teamName = { heracles: meta.heraclesTeam, opponent: meta.opponentTeam };
  const dec = data?.metric?.dec ?? 1;

  const presetLabel = PRESETS.find(p => p.id === preset)?.label ?? preset;
  const exportButton = (
    <button style={{ ...ctlBtn(false), opacity: data && !exporting ? 1 : 0.5 }} onClick={() => downloadPng('page')} disabled={!data || exporting} title="Download the whole analysis as one PNG">
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Download size={14} /> {exporting ? 'Exporting…' : 'Export page (PNG)'}</span>
    </button>
  );

  return shell(
    <div ref={pageRef} data-export-root="true" style={{ width: '100%', maxWidth: 1100, display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', background: PAGE_BG, boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ ...cardStyle, background: INK, padding: '20px 28px' }}>
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: 26, fontWeight: 700, color: '#FFF', lineHeight: 1.1 }}>
          Period Comparison — {meta.home} v {meta.away}
        </div>
        <div style={{ fontFamily: FONT_MONO, fontSize: 11, color: MUTED_DARK, letterSpacing: '.06em', marginTop: 4 }}>
          {String(meta.date).slice(0, 10)} · {meta.scoreHome} – {meta.scoreAway} · {periods.length} five-minute periods · outfield players only
        </div>
        {/* The selection, so an exported image explains itself without the controls. */}
        <div style={{ fontFamily: FONT_MONO, fontSize: 11, color: MUTED_DARK, letterSpacing: '.06em', marginTop: 2 }}>
          Blocks: {presetLabel} ({blocks.length}) · {LEVELS.find(l => l.id === level)?.label} · {UNIT_OPTIONS.find(u => u.id === unit)?.label} · {basis === 'heralabx' ? 'HeraLabX-equivalent' : 'KNVB values'}
        </div>
      </div>

      {/* Controls — interactive only, never part of an export */}
      <div data-html2canvas-ignore="true" style={{ ...cardStyle, padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <Segmented options={LEVELS} value={level} onChange={setLevel} />
          <select value={metric} onChange={e => setMetric(e.target.value)} style={selStyle}>
            {METRIC_OPTIONS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          <select value={unit} onChange={e => setUnit(e.target.value)} style={selStyle}>
            {UNIT_OPTIONS.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
          </select>
          <div style={{ marginLeft: 'auto' }}>
            <Segmented options={[{ id: 'heralabx', label: 'HeraLabX' }, { id: 'knvb', label: 'KNVB' }]} value={basis} onChange={setBasis} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ ...monoNote, marginRight: 4 }}>BLOCKS</span>
          <Segmented options={PRESETS} value={preset} onChange={id => (id === 'custom' ? enterCustom() : setPreset(id))} />
        </div>

        {/* Period strip */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', userSelect: 'none' }}>
          {periods.map(p => {
            const owner = ownerOf.get(p.index);
            const isPending = pending.includes(p.index);
            return (
              <div
                key={p.index}
                onMouseDown={e => onChipMouseDown(p, e)}
                onMouseEnter={() => onChipMouseEnter(p)}
                title={owner !== undefined ? `${blocks[owner].label} — click to take it out of the block` : 'Click or drag to select; shift-click for a range'}
                style={{
                  padding: '5px 8px', borderRadius: 4, cursor: 'pointer', fontFamily: FONT_MONO, fontSize: 11,
                  background: isPending ? '#DBEAFE' : TRACK_BG,
                  color: owner !== undefined || isPending ? INK : MUTED_LIGHT,
                  borderLeft: `4px solid ${owner !== undefined ? blockColor(owner) : 'transparent'}`,
                  outline: isPending ? '2px solid #3b82f6' : 'none',
                  marginRight: p.half === 1 && periods[p.index + 1]?.half === 2 ? 10 : 0,
                }}
              >
                {p.label}
              </div>
            );
          })}
        </div>

        {preset === 'custom' && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              style={{ ...ctlBtn(pending.length > 0 && customBlocks.length < MAX_CUSTOM_BLOCKS), opacity: pending.length && customBlocks.length < MAX_CUSTOM_BLOCKS ? 1 : 0.5 }}
              disabled={!pending.length || customBlocks.length >= MAX_CUSTOM_BLOCKS}
              onClick={addBlock}
            >
              Add block{pending.length ? ` (${pending.length})` : ''}
            </button>
            {customBlocks.length >= MAX_CUSTOM_BLOCKS && <span style={monoNote}>max. {MAX_CUSTOM_BLOCKS} blocks</span>}
            {customBlocks.map((b, i) => (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 4, borderLeft: `4px solid ${blockColor(i)}`, background: TRACK_BG, borderRadius: 4, padding: '2px 4px 2px 6px' }}>
                <input
                  defaultValue={b.label}
                  key={`${b.id}-${b.label}`}
                  onBlur={e => { if (e.target.value.trim() !== b.label) renameBlock(i, e.target.value.trim()); }}
                  onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  style={{ width: Math.max(60, b.label.length * 8), border: 'none', background: 'transparent', fontFamily: FONT_BODY, fontSize: 12, color: INK, outline: 'none' }}
                />
                <button onClick={() => removeBlock(i)} title="Remove block" style={{ background: 'none', border: 'none', cursor: 'pointer', color: MUTED_LIGHT, padding: 2, display: 'flex' }}><X size={12} /></button>
              </div>
            ))}
            {!customBlocks.length && !pending.length && <span style={monoNote}>Select periods and click "Add block".</span>}
          </div>
        )}
      </div>

      {error && <div data-html2canvas-ignore="true" style={{ maxWidth: 1100, width: '100%' }}><Note tone="warn">{error}</Note></div>}

      {data && (
        <>
          <PeriodShape data={data} blocks={blocks} dec={dec} teamName={teamName} />
          {level === 'player'
            ? <PlayerTable data={data} dec={dec} teamName={teamName} sort={sort} setSort={setSort} teamFilter={teamFilter} setTeamFilter={setTeamFilter} lineFilter={lineFilter} setLineFilter={setLineFilter} />
            : (
              <div ref={exportRef} style={{ ...cardStyle, padding: '18px 24px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <div style={sectionTitle}>{data.metric.label} per block</div>
                  <div style={monoNote}>{data.metric.sub}</div>
                  <div style={{ marginLeft: 'auto' }}>{exportButtons('chart')}</div>
                </div>
                <Legend teamName={teamName} />
                {level === 'team'
                  ? <TeamBlocks data={data} dec={dec} names={names} />
                  : <LineBlocks data={data} dec={dec} names={names} />}
                <DeltaStrip data={data} teamName={teamName} />
                <Footer data={data} />
              </div>
            )}
          {level === 'player' && <div style={{ maxWidth: 1100, width: '100%' }}><DeltaStrip data={data} teamName={teamName} standalone /></div>}
          {data.blockSummaries?.length > 0 && (
            <BlockCards
              data={data} blocks={blocks} teamName={teamName} names={names}
              cardsRef={cardsRef} actions={exportButtons('cards')}
              context={`${meta.home} v ${meta.away} · ${String(meta.date).slice(0, 10)} · ${meta.scoreHome} – ${meta.scoreAway} · Blocks: ${presetLabel} (${blocks.length})`}
            />
          )}
          <Notes data={data} />
          {level === 'player' && <div style={{ maxWidth: 1100, width: '100%' }}><Footer data={data} /></div>}
        </>
      )}
      {loading && !data && <div style={{ color: 'var(--text-dimmed)', fontSize: 14 }}>Loading…</div>}
    </div>,
    exportButton,
  );
}

// The Team Comparison card's layout, one card per block: all five metrics at team level, each
// with its own conversion factor (server-side), leader pill + % vs the mean, and the tally.
// `context` names the match and selection, so the section's own PNG explains itself.
function BlockCards({ data, blocks, teamName, names, cardsRef, actions, context }) {
  const code = { heracles: teamAbbrev(teamName.heracles), opponent: teamAbbrev(teamName.opponent) };
  const colorIndex = new Map(blocks.map((b, i) => [b.id, i]));
  return (
    <div ref={cardsRef} data-export-cards="true" style={{ maxWidth: 1100, width: '100%', display: 'flex', flexDirection: 'column', gap: 10, background: PAGE_BG, boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <div style={sectionTitle}>All metrics per block</div>
            <div style={monoNote}>team · {data.unit.label} · {data.basis === 'heralabx' ? 'HeraLabX-equivalent, each metric with its own factor' : 'KNVB values'}</div>
          </div>
          {context && <div style={{ ...monoNote, marginTop: 2 }}>{context}</div>}
        </div>
        {actions && <div style={{ marginLeft: 'auto' }}>{actions}</div>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16 }}>
        {data.blockSummaries.map(s => (
          <div key={s.blockId} style={{ ...cardStyle, maxWidth: 'none', overflow: 'hidden' }}>
            <div style={{ background: INK, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 5, alignSelf: 'stretch', background: blockColor(colorIndex.get(s.blockId) ?? 0), borderRadius: 2 }} />
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 700, color: '#FFF' }}>{s.label}</div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: 10, fontFamily: FONT_DISPLAY }}>
                <span style={{ fontSize: 30, fontWeight: 700, color: '#E8607C' }}>{s.tally.heracles}</span>
                <span style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '.1em', color: MUTED_DARK }}>{s.tally.tie} TIED</span>
                <span style={{ fontSize: 30, fontWeight: 700, color: '#6E9BC8' }}>{s.tally.opponent}</span>
              </div>
            </div>
            <div style={{ padding: '2px 20px 14px' }}>
              {s.metrics.map(m => {
                const leaderColor = m.leader === 'tie' ? TIE_COLOR : SIDE_COLOR[m.leader];
                const max = Math.max(m.heracles ?? 0, m.opponent ?? 0) || 1;
                return (
                  <div key={m.key} style={{ padding: '10px 0', borderBottom: `1px solid ${HAIRLINE}`, display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 600, color: INK }}>{m.label}</div>
                      <div style={monoNote}>{m.sub}</div>
                      {m.leader && (
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontFamily: FONT_DISPLAY, fontSize: 12, fontWeight: 700, letterSpacing: '.08em', color: '#FFF', background: leaderColor, padding: '1px 6px', borderRadius: 3 }}>{m.leader === 'tie' ? 'TIE' : names[m.leader]}</span>
                          <span style={{ width: 52, textAlign: 'right', fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 700, color: leaderColor }}>{m.deltaLabel}</span>
                        </div>
                      )}
                    </div>
                    {['heracles', 'opponent'].map(side => (
                      <div key={side} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 32, ...monoNote }}>{code[side]}</div>
                        <div style={{ flex: 1, height: 20, background: TRACK_BG, borderRadius: 3, position: 'relative' }}>
                          <div style={{
                            position: 'absolute', inset: '0 auto 0 0', width: `${m[side] == null ? 0 : (m[side] / max) * 100}%`,
                            background: SIDE_COLOR[side], borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                            paddingRight: 8, boxSizing: 'border-box',
                            boxShadow: m.leader === side ? LEADER_RING : 'none',
                          }}>
                            <span style={{ fontFamily: FONT_DISPLAY, fontSize: 14, fontWeight: 700, color: '#FFF' }}>{fmtVal(m[side], m.dec)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {data.basis === 'heralabx' && !m.converted && <div style={monoNote}>not converted — no GPS reference for this metric</div>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Legend({ teamName }) {
  return (
    <div style={{ display: 'flex', gap: 14, margin: '6px 0 4px', fontFamily: FONT_BODY, fontSize: 12, color: SECONDARY_LIGHT }}>
      {['heracles', 'opponent'].map(s => (
        <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: SIDE_COLOR[s] }} />{teamName[s]}
        </span>
      ))}
      <span style={monoNote}>bars to true scale from 0 · outline + label = leader (difference vs the mean of both)</span>
    </div>
  );
}

// (a) The match's shape: every period, both teams, blocks shaded.
function PeriodShape({ data, blocks, dec, teamName }) {
  const chartData = data.periodSeries.map(p => ({ index: p.index, label: p.label, heracles: p.heracles, opponent: p.opponent }));
  const labelOf = new Map(chartData.map(d => [d.index, d.label]));
  const n = chartData.length;
  return (
    <div style={{ ...cardStyle, padding: '16px 20px 8px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <div style={sectionTitle}>Match shape</div>
        <div style={monoNote}>{data.metric.label} · {data.metric.sub} · team · per 5-minute period</div>
      </div>
      <div style={{ height: 260, marginTop: 8 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 24, left: 0 }}>
            <CartesianGrid stroke={HAIRLINE} vertical={false} />
            {blocks.flatMap((b, i) => runs(b.periodIndexes).map(([a, z]) => (
              <ReferenceArea key={`${b.id}-${a}`} x1={a - 0.5} x2={z + 0.5} fill={blockColor(i)} fillOpacity={0.12} strokeOpacity={0} ifOverflow="extendDomain" />
            )))}
            <XAxis
              type="number" dataKey="index" domain={[-0.5, n - 0.5]} ticks={chartData.map(d => d.index)} interval={0}
              tickFormatter={i => labelOf.get(i) ?? ''} tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', fill: MUTED_LIGHT }}
              angle={-40} textAnchor="end" height={44}
            />
            <YAxis tick={{ fontSize: 10, fill: MUTED_LIGHT }} width={48} tickFormatter={v => fmtVal(v, dec >= 2 ? 1 : 0)} />
            <Tooltip
              labelFormatter={i => labelOf.get(i)}
              formatter={(v, key) => [fmtVal(v, dec), teamName[key]]}
              contentStyle={{ fontFamily: 'Barlow, sans-serif', fontSize: 12 }}
            />
            <Line type="monotone" dataKey="heracles" stroke={HERACLES} strokeWidth={2.5} dot={{ r: 2.5 }} connectNulls={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="opponent" stroke={OPPONENT} strokeWidth={2.5} dot={{ r: 2.5 }} connectNulls={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// (b) Team level: one bar pair per block.
function TeamBlocks({ data, dec, names }) {
  const max = Math.max(...data.blocks.flatMap(b => [b.teamHeracles ?? 0, b.teamOpponent ?? 0]));
  return (
    <div style={{ display: 'flex', gap: 18, overflowX: 'auto', padding: '10px 0 4px' }}>
      {data.blocks.map((b, i) => {
        const cmp = data.comparisons.byBlock[i];
        return (
          <div key={b.id} style={{ minWidth: 104, flex: '1 0 104px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <LeaderPill cmp={cmp} names={names} />
            <BarPair heracles={b.teamHeracles} opponent={b.teamOpponent} leader={cmp.leader} max={max} dec={dec} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <span style={{ width: 4, height: 14, background: blockColor(i), borderRadius: 1 }} />
              <span style={{ fontFamily: FONT_DISPLAY, fontSize: 15, fontWeight: 600, color: INK }}>{b.label}</span>
            </div>
            <div style={{ ...monoNote, textAlign: 'center', lineHeight: 1.5 }}>
              <span style={{ color: HERACLES }}>{fmtMin(b.minutesHeracles)} · n{b.nHeracles}</span><br />
              <span style={{ color: OPPONENT }}>{fmtMin(b.minutesOpponent)} · n{b.nOpponent}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// (b) Line level: per block, a bar pair per line.
function LineBlocks({ data, dec, names }) {
  const values = data.blocks.flatMap(b => b.rows.map(r => r.value ?? 0));
  const max = Math.max(0, ...values);
  return (
    <div style={{ display: 'flex', gap: 22, overflowX: 'auto', padding: '10px 0 4px' }}>
      {data.blocks.map((b, i) => (
        <div key={b.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, borderLeft: i ? `1px solid ${HAIRLINE}` : 'none', paddingLeft: i ? 18 : 0 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            {LINE_OPTIONS.map(l => {
              const cmp = data.comparisons.byEntity.find(e => e.blockId === b.id && e.entityId === l.id);
              if (!cmp) return null;
              return (
                <div key={l.id} style={{ minWidth: 92, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <LeaderPill cmp={cmp} names={names} />
                  <BarPair heracles={cmp.heracles} opponent={cmp.opponent} leader={cmp.leader} max={max} dec={dec} height={150} />
                  <div style={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 600, color: INK }}>{l.label}</div>
                  <div style={monoNote}>
                    <span style={{ color: HERACLES }}>n{cmp.nHeracles}</span> v <span style={{ color: OPPONENT }}>n{cmp.nOpponent}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <span style={{ width: 4, height: 14, background: blockColor(i), borderRadius: 1 }} />
            <span style={{ fontFamily: FONT_DISPLAY, fontSize: 15, fontWeight: 600, color: INK }}>{b.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// (c) Each team's change from one block to the next — kept visually apart from the
// Heracles-vs-opponent percentages above (different derivation: relative to the earlier block).
function DeltaStrip({ data, teamName, standalone = false }) {
  if (data.blocks.length < 2) return null;
  const labelOf = new Map(data.blocks.map(b => [b.id, b.label]));
  const teamDeltas = data.blockDeltas.filter(d => d.entityId === 'team');
  const lineDeltas = data.level === 'line' ? data.blockDeltas.filter(d => d.entityId !== 'team') : [];
  const lineLabel = new Map(LINE_OPTIONS.map(l => [l.id, l.label]));
  const chip = d => (
    <span key={`${d.fromBlockId}-${d.toBlockId}-${d.side}-${d.entityId}`} style={{ fontFamily: FONT_BODY, fontSize: 12, color: INK, background: TRACK_BG, borderRadius: 4, padding: '3px 8px', borderLeft: `3px solid ${SIDE_COLOR[d.side]}`, whiteSpace: 'nowrap' }}>
      {d.entityId === 'team' ? teamName[d.side] : `${teamName[d.side]} · ${lineLabel.get(d.entityId)}`}{' '}
      {labelOf.get(d.toBlockId)} <b style={{ color: SIDE_COLOR[d.side] }}>{d.deltaLabel}</b> vs {labelOf.get(d.fromBlockId)}
    </span>
  );
  const body = (
    <>
      <div style={{ ...monoNote, marginBottom: 6 }}>CHANGE PER TEAM VS THE PREVIOUS BLOCK</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{teamDeltas.map(chip)}</div>
      {lineDeltas.length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>{lineDeltas.map(chip)}</div>}
    </>
  );
  if (standalone) return <div style={{ ...cardStyle, padding: '12px 20px' }}>{body}</div>;
  return <div style={{ borderTop: `1px solid ${HAIRLINE}`, marginTop: 10, paddingTop: 10 }}>{body}</div>;
}

function Footer({ data }) {
  let conv;
  if (data.basis === 'knvb') conv = 'KNVB values, not converted';
  else if (!data.converted) conv = 'Not converted — no GPS reference for this metric; KNVB values';
  else {
    const src = FACTOR_SOURCE_LABEL[data.factor.source] + (data.factor.source === 'pooled' && data.factor.n ? `, n=${data.factor.n}` : '');
    conv = `Both teams converted to HeraLabX (factor ${data.factor.value.toFixed(3)}, ${src})`;
  }
  return <div style={{ ...monoNote, paddingTop: 10 }}>{conv} · outfield players only · periods on the match clock</div>;
}

function Notes({ data }) {
  const { notes } = data;
  if (!notes.fullMatchOverlap && !notes.minuteImbalance && !notes.formationWarning) return null;
  return (
    <div style={{ maxWidth: 1100, width: '100%', display: 'flex', flexDirection: 'column', gap: 6 }}>
      {notes.fullMatchOverlap && <Note>Periods overlap by ~2 s; blocks add up to ~0.5–1% more than the match total.</Note>}
      {notes.minuteImbalance && <Note tone="warn">Player-minutes per block differ by more than 2% between the teams, so block totals are not a fair comparison. Use a "per player" unit instead.</Note>}
      {notes.formationWarning && <Note tone="warn">Lines follow the position labels within each team's formation. The number of players per line can differ (see n) and the roles are not identical: normalising per player corrects for group size, not for role.</Note>}
    </div>
  );
}

// Player level: sortable table, value + minutes per block, block-to-block deltas.
function PlayerTable({ data, dec, teamName, sort, setSort, teamFilter, setTeamFilter, lineFilter, setLineFilter }) {
  const players = useMemo(() => {
    const map = new Map();
    data.blocks.forEach((b, bi) => {
      for (const r of b.rows) {
        const key = `${r.side}|${r.entityId}`;
        if (!map.has(key)) map.set(key, { key, side: r.side, entityId: r.entityId, name: r.entityLabel, position: r.position, line: r.line, cells: [] });
        map.get(key).cells[bi] = r;
      }
    });
    const deltaOf = new Map(data.blockDeltas.filter(d => d.entityId !== 'team').map(d => [`${d.side}|${d.entityId}|${d.fromBlockId}`, d]));
    return [...map.values()].map(p => ({
      ...p,
      deltas: data.blocks.slice(1).map((b, i) => deltaOf.get(`${p.side}|${p.entityId}|${data.blocks[i].id}`) || null),
    }));
  }, [data]);

  const lineLabel = new Map(LINE_OPTIONS.map(l => [l.id, l.label]));
  const visible = players
    .filter(p => teamFilter === 'all' || p.side === teamFilter)
    .filter(p => lineFilter === 'all' || p.line === lineFilter);

  const sortValue = (p, key) => {
    if (key === 'name') return p.name;
    if (key === 'team') return teamName[p.side];
    if (key === 'line') return lineLabel.get(p.line);
    if (key.startsWith('b')) return p.cells[Number(key.slice(1))]?.value ?? null;
    if (key.startsWith('d')) return p.deltas[Number(key.slice(1))]?.pctDiff ?? null;
    return null;
  };
  const sorted = [...visible].sort((a, b) => {
    const va = sortValue(a, sort.key), vb = sortValue(b, sort.key);
    if (va === null && vb === null) return 0;
    if (va === null) return 1; // missing always last
    if (vb === null) return -1;
    const c = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
    return sort.dir === 'asc' ? c : -c;
  });
  const header = (key, label, align = 'right') => (
    <th
      onClick={() => setSort(s => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }))}
      style={{ textAlign: align, padding: '6px 8px', cursor: 'pointer', whiteSpace: 'nowrap', color: sort.key === key ? INK : MUTED_LIGHT }}
    >
      {label}{sort.key === key ? (sort.dir === 'desc' ? ' ▾' : ' ▴') : ''}
    </th>
  );

  return (
    <div style={{ ...cardStyle, padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={sectionTitle}>{data.metric.label} per player</div>
        <div style={monoNote}>{data.metric.sub}</div>
        <div data-html2canvas-ignore="true" style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Segmented options={[{ id: 'all', label: 'Both teams' }, { id: 'heracles', label: teamName.heracles }, { id: 'opponent', label: teamName.opponent }]} value={teamFilter} onChange={setTeamFilter} />
          <select value={lineFilter} onChange={e => setLineFilter(e.target.value)} style={selStyle}>
            <option value="all">All lines</option>
            {LINE_OPTIONS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT_BODY, fontSize: 13 }}>
          <thead>
            <tr style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>
              {header('name', 'Player', 'left')}
              {header('team', 'Team', 'left')}
              {header('line', 'Line', 'left')}
              {data.blocks.map((b, i) => (
                <React.Fragment key={b.id}>
                  {header(`b${i}`, b.label)}
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: MUTED_LIGHT, fontWeight: 400 }}>min</th>
                </React.Fragment>
              ))}
              {data.blocks.slice(1).map((b, i) => header(`d${i}`, `Δ ${b.label}`))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(p => (
              <tr key={p.key} style={{ borderTop: `1px solid ${HAIRLINE}` }}>
                <td style={{ padding: '6px 8px', borderLeft: `3px solid ${SIDE_COLOR[p.side]}`, whiteSpace: 'nowrap' }}>{p.name}</td>
                <td style={{ padding: '6px 8px', color: SIDE_COLOR[p.side], whiteSpace: 'nowrap' }}>{teamAbbrev(teamName[p.side])}</td>
                <td style={{ padding: '6px 8px', color: MUTED_LIGHT, whiteSpace: 'nowrap' }} title={p.position}>{lineLabel.get(p.line)}</td>
                {data.blocks.map((b, i) => {
                  const c = p.cells[i];
                  const low = c?.lowMinutes;
                  const tip = low ? 'Less than 1 minute in this block — value not reliable' : undefined;
                  return (
                    <React.Fragment key={b.id}>
                      <td title={tip} style={{ padding: '6px 8px', textAlign: 'right', fontFamily: FONT_DISPLAY, fontSize: 15, fontWeight: 600, color: INK, opacity: low ? 0.35 : 1 }}>{fmtVal(c?.value ?? null, dec)}</td>
                      <td title={tip} style={{ padding: '6px 8px', textAlign: 'right', ...monoNote, opacity: low ? 0.35 : 1 }}>{c && c.minutes > 0 ? c.minutes.toFixed(1) : '–'}</td>
                    </React.Fragment>
                  );
                })}
                {p.deltas.map((d, i) => (
                  <td key={i} style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: d?.pctDiff == null ? MUTED_LIGHT : d.pctDiff < 0 ? '#B42318' : '#067647' }}>{d?.deltaLabel ?? '–'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ ...monoNote, paddingTop: 10 }}>
        – = did not play in this block · greyed = less than 1 minute · Δ = change vs the previous block
      </div>
    </div>
  );
}
